"use client";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Form, Checkbox } from "antd";
import { DownOutlined } from "@ant-design/icons";
import "./custom-select.scss";

interface CustomSelectProps {
  label: string;
  placeholder: string;
  options: { value: string; label: string }[];
  value: any[];
  onChange: (value: any[]) => void;
  required?: boolean;
  className?: string;
  validateStatus?: "success" | "error" | "warning" | "validating" | "";
  help?: string | null;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  required = false,
  className = "",
  validateStatus,
  help,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionChange = (optionValue: any, checked: boolean) => {
    const newValue = checked
      ? [...value, optionValue]
      : value.filter((v) => v !== optionValue);
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1)
      return (
        options.find((opt) => opt.value === value[0])?.label || placeholder
      );
    return `${value.length} seleccionado${value.length > 1 ? "s" : ""}`;
  };

  return (
    <Form.Item
      label={label}
      validateStatus={
        validateStatus || (required && value.length === 0 ? "error" : "success")
      }
      help={
        help !== undefined
          ? help
          : required && value.length === 0
          ? `${label} es obligatorio`
          : null
      }
      className={`custom-form-item ${className}`}
    >
      <div className="custom-select-container" ref={containerRef}>
        <div
          className={`custom-select ${open ? "open" : ""} ${
            validateStatus === "error" ? "error" : ""
          }`}
          onClick={() => setOpen(!open)}
        >
          <div className="select-content">
            <span className={value.length === 0 ? "placeholder" : ""}>
              {getDisplayText()}
            </span>
            <DownOutlined className="select-arrow" />
          </div>

          {open && (
            <div className="custom-dropdown-menu">
              {options.map((option) => (
                <div
                  key={option.value}
                  className="custom-option"
                  onClick={() => {
                    const isChecked = value.includes(option.value);
                    handleOptionChange(option.value, !isChecked);
                  }}
                >
                  <Checkbox
                    checked={value.includes(option.value)}
                    onChange={(e) =>
                      handleOptionChange(option.value, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()} // Prevent double triggering
                  />
                  <span className="option-label">{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Form.Item>
  );
};

export default CustomSelect;
